import React, { useState, useEffect, useMemo } from "react";
import { Box, Container, Paper, Stepper, Step, StepLabel, Button, Typography } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { useOrganization } from "../../contexts/OrganizationContext";
import { useChallengesTablePaginated } from "../../api/hooks/useChallenges";
import { usePrograms, useAllProgramVersionsDetailed } from "../../api/hooks/program.hooks";
import { useAllProgramVersionItems } from "../../api/hooks/useProgramVersionItems";
import { ChallengeTableProgramVersionItem } from "../../api/types/dtos/challenge/ChallengeDto";
import LoadingSpinner from "../../components/LoadingSpinner";
import {
  ReportBuilderState,
  SelectedItemComment,
  DirectMapping,
  ReportCell,
  CellMapping,
} from "./report-builder/types/reportBuilder.types";
import FieldSelectionStep from "./report-builder/components/FieldSelectionStep";
import QuestionSelectionStep from "./report-builder/components/QuestionSelectionStep";
import DirectMappingStep from "./report-builder/components/DirectMappingStep";
import CellCreationStep from "./report-builder/components/CellCreationStep";
import CellMappingStep from "./report-builder/components/CellMappingStep";
import PreviewStep from "./report-builder/components/PreviewStep";
import ProcessingStep from "./report-builder/components/ProcessingStep";
import TemplateManager from "./report-builder/components/TemplateManager";
import { getAllQuestions } from "./report-builder/utils/reportBuilder.parser";
import {
  extractFormIdsFromPVIs,
  createPviToFormIdMap,
} from "./report-builder/utils/reportBuilder.parser";
import { useFormsByIds } from "../../api/hooks/useForms";
import { useAnswersByItemIds } from "../../api/hooks/useAnswers";
import {
  useCreateReportTemplate,
  useUpdateReportTemplate,
} from "../../api/hooks/useReportTemplates";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
} from "@mui/material";

const STEPS_FULL = [
  "Seleccionar Campos",
  "Seleccionar Preguntas",
  "Mapeo Directo",
  "Crear Celdas",
  "Mapear Valores",
  "Vista Previa",
  "Procesar y Exportar",
];

const STEPS_TEMPLATE = STEPS_FULL.slice(0, 5);

const STORAGE_KEY = "reportBuilderState";

const ReportBuilderPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const isTemplateMode = searchParams.get("mode") === "template";
  const steps = isTemplateMode ? STEPS_TEMPLATE : STEPS_FULL;

  const [activeStep, setActiveStep] = useState(0);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateDescription, setTemplateDescription] = useState("");
  const [reportState, setReportState] = useState<ReportBuilderState>({
    version: "1.0",
    selectedFields: [],
    selectedQuestions: [],
    selectedItemComments: [],
    directMappings: [],
    cells: [],
    cellMappings: [],
  });

  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.id || "";
  const [templateId, setTemplateId] = useState<string | null>(null);
  const createTemplate = useCreateReportTemplate();
  const updateTemplate = useUpdateReportTemplate();

  // Fetch challenges data (only in report mode - template mode uses lightweight hook)
  const { data: challengesData, isLoading: isLoadingChallenges } = useChallengesTablePaginated(
    organizationId || "placeholder",
    { page: 0, size: 10000 },
    { enabled: !!organizationId && !isTemplateMode }
  );

  // Lightweight fetch for template mode: only program version items, no challenge/inspection data
  const { data: templatePviData, isLoading: isLoadingTemplatePvis } = useAllProgramVersionItems(
    organizationId || "placeholder"
  );

  // Fetch programs and versions
  const { data: programs = [] } = usePrograms(organizationId);
  const { data: programVersions = [] } = useAllProgramVersionsDetailed(organizationId);

  // Unified program version items array from either source
  const programVersionItems: ChallengeTableProgramVersionItem[] = useMemo(() => {
    if (isTemplateMode) {
      return (templatePviData || []) as unknown as ChallengeTableProgramVersionItem[];
    }
    return challengesData?.data?.programVersionItems || [];
  }, [isTemplateMode, templatePviData, challengesData]);

  const isLoadingData = isTemplateMode ? isLoadingTemplatePvis : isLoadingChallenges;

  // Extract form IDs and batch fetch forms
  const formIds = useMemo(() => {
    if (!programVersionItems || programVersionItems.length === 0) return [];
    return extractFormIdsFromPVIs(programVersionItems);
  }, [programVersionItems]);

  const { data: formsMap } = useFormsByIds(organizationId, formIds);

  const pviToFormIdMap = useMemo(() => {
    if (!programVersionItems || programVersionItems.length === 0) return new Map();
    return createPviToFormIdMap(programVersionItems);
  }, [programVersionItems]);

  // Load state from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {


        // Force clear for now - old stringId format will cause errors
        console.log("Clearing old report builder state to migrate to UUID format");
        localStorage.removeItem(STORAGE_KEY);

        // Reset to empty state
        setReportState({
          version: "1.0",
          selectedFields: [],
          selectedQuestions: [],
          selectedItemComments: [],
          directMappings: [],
          cells: [],
          cellMappings: [],
        });
      }
    } catch (e) {
      console.error("Error loading saved state:", e);
    }
  }, []);

  // Save state to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(reportState));
    } catch (e) {
      console.error("Error saving state:", e);
    }
  }, [reportState]);

  // Build selected questions with full metadata
  const selectedQuestionsWithMetadata = useMemo(() => {
    if (!programVersionItems || programVersionItems.length === 0 || !formsMap) return [];

    return reportState.selectedQuestions.map((sq) => {
      const pvi = programVersionItems.find(
        (item) => item.id === sq.programVersionItemId
      );
      if (!pvi) return sq;

      const formId = pviToFormIdMap.get(pvi.id);
      if (!formId || !formsMap[formId]) return sq;

      const form = formsMap[formId];
      const questions = getAllQuestions(form);
      const question = questions.find((q) => q.id === sq.questionId);

      return {
        ...sq,
        question: question || sq.question,
        programVersionItem: pvi,
      };
    });
  }, [reportState.selectedQuestions, programVersionItems, formsMap, pviToFormIdMap]);

  // Extract all unique inspection program item IDs from challenges (report mode only)
  const answerItemIds = useMemo(() => {
    if (isTemplateMode || !challengesData?.data?.challenges) return [];

    const itemIdSet = new Set<string>();
    challengesData.data.challenges.forEach((challenge) => {
      challenge.inspectionsProgramsVersionsItems.forEach((item) => {
        if (item.id) {
          itemIdSet.add(item.id);
        }
      });
    });

    return Array.from(itemIdSet);
  }, [isTemplateMode, challengesData]);

  // Batch load answers for all items
  const { data: answersMap } = useAnswersByItemIds(organizationId, answerItemIds);

  const handleNext = () => {
    // Validate that current step is complete before proceeding
    if (!canProceed()) {
      return;
    }

    // Special validation for step 3 -> step 4
    // Ensure all previous steps are complete
    if (activeStep === 3) {
      const allPreviousStepsComplete =
        isStepComplete(0) && isStepComplete(1) && isStepComplete(2) && isStepComplete(3);

      if (!allPreviousStepsComplete) {
        alert("Por favor completa todos los pasos anteriores antes de continuar.");
        return;
      }
    }

    if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1);
    } else if (isTemplateMode) {
      setSaveDialogOpen(true);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  const handleReset = () => {
    setReportState({
      version: "1.0",
      selectedFields: [],
      selectedQuestions: [],
      selectedItemComments: [],
      directMappings: [],
      cells: [],
      cellMappings: [],
    });
    setActiveStep(0);
    localStorage.removeItem(STORAGE_KEY);
  };

  const handleExportConfig = () => {
    const config = {
      ...reportState,
      version: "1.0",
    };
    const blob = new Blob([JSON.stringify(config, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "report_config.json");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleImportConfig = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const config = JSON.parse(e.target?.result as string);
        if (config.version && config.selectedFields && config.cells) {
          setReportState(config);
          setTemplateId(null);
          setActiveStep(0);
        } else {
          alert("El archivo de configuración no es válido.");
        }
      } catch {
        alert("Error al leer el archivo de configuración.");
      }
    };
    reader.readAsText(file);
  };

  const handleLoadTemplate = (templateJson: string) => {
    try {
      const config = JSON.parse(templateJson);
      if (config.version && config.selectedFields && config.cells) {
        setReportState(config);
        setTemplateId(null);
        setActiveStep(0);
      } else {
        alert("La plantilla no es válida.");
      }
    } catch {
      alert("Error al cargar la plantilla.");
    }
  };

  const handleTemplateSaved = (savedTemplateId: string) => {
    setTemplateId(savedTemplateId);
  };

  const getStepContent = () => {
    if (isLoadingData || (!isTemplateMode && !challengesData?.data) || (isTemplateMode && !templatePviData)) {
      return <LoadingSpinner />;
    }

    const challenges = challengesData?.data?.challenges || [];

    switch (activeStep) {
      case 0:
        return (
          <FieldSelectionStep
            selectedFields={reportState.selectedFields}
            onFieldsChange={(fields) => setReportState({ ...reportState, selectedFields: fields })}
            onImportConfig={handleImportConfig}
          />
        );

      case 1:
        return (
          <QuestionSelectionStep
            programVersionItems={programVersionItems}
            selectedQuestions={reportState.selectedQuestions}
            selectedItemComments={reportState.selectedItemComments}
            onQuestionsChange={(questions) =>
              setReportState({ ...reportState, selectedQuestions: questions })
            }
            onItemCommentsChange={(comments: SelectedItemComment[]) =>
              setReportState({ ...reportState, selectedItemComments: comments })
            }
            programs={programs}
            programVersions={programVersions}
            organizationId={organizationId}
          />
        );

      case 2:
        return (
          <DirectMappingStep
            selectedFields={reportState.selectedFields}
            selectedQuestions={selectedQuestionsWithMetadata}
            selectedItemComments={reportState.selectedItemComments}
            directMappings={reportState.directMappings}
            onDirectMappingsChange={(mappings: DirectMapping[]) =>
              setReportState({ ...reportState, directMappings: mappings })
            }
            onApplyDirectMappings={(newCells: ReportCell[], newMappings: CellMapping[]) => {
              setReportState((prev) => ({
                ...prev,
                cells: [...prev.cells, ...newCells],
                cellMappings: [...prev.cellMappings, ...newMappings],
              }));
            }}
          />
        );

      case 3:
        return (
          <CellCreationStep
            cells={reportState.cells}
            onCellsChange={(cells) => setReportState({ ...reportState, cells })}
          />
        );

      case 4:
        return (
          <CellMappingStep
            cells={reportState.cells}
            cellMappings={reportState.cellMappings}
            onMappingsChange={(mappings) =>
              setReportState({ ...reportState, cellMappings: mappings })
            }
            selectedFields={reportState.selectedFields}
            selectedQuestions={selectedQuestionsWithMetadata}
            selectedItemComments={reportState.selectedItemComments}
          />
        );

      case 5:
        return (
          <PreviewStep
            cells={reportState.cells}
            cellMappings={reportState.cellMappings}
            selectedFields={reportState.selectedFields}
            selectedQuestions={selectedQuestionsWithMetadata}
            challenges={challenges}
            answersMap={answersMap}
            onExportConfig={handleExportConfig}
            onImportConfig={handleImportConfig}
          />
        );

      case 6:
        return (
          <ProcessingStep
            cells={reportState.cells}
            cellMappings={reportState.cellMappings}
            challenges={challenges}
            initialFilters={reportState.initialFilters}
            answersMap={answersMap}
            onComplete={handleReset}
            templateId={templateId}
          />
        );

      default:
        return "Unknown step";
    }
  };

  const canProceed = () => {
    switch (activeStep) {
      case 0:
        return reportState.selectedFields.length > 0;
      case 1:
        return (
          reportState.selectedQuestions.length > 0 || reportState.selectedItemComments.length > 0
        );
      case 2:
        return true; // Direct mapping step is optional
      case 3:
        return reportState.cells.length > 0;
      case 4:
        return (
          reportState.cells.length > 0 &&
          reportState.cellMappings.some(
            (m) => m.sourceType !== "default" || (m.sourceType === "default" && m.defaultValue)
          )
        );
      case 5:
        return true; // Preview step always allows proceeding
      case 6:
        return false; // Processing step doesn't have next button (report mode only)
      default:
        return false;
    }
  };

  const isStepComplete = (step: number): boolean => {
    switch (step) {
      case 0:
        return reportState.selectedFields.length > 0;
      case 1:
        return (
          reportState.selectedQuestions.length > 0 || reportState.selectedItemComments.length > 0
        );
      case 2:
        return true; // Direct mapping is optional
      case 3:
        return reportState.cells.length > 0;
      case 4:
        return (
          reportState.cells.length > 0 &&
          reportState.cellMappings.some(
            (m) => m.sourceType !== "default" || (m.sourceType === "default" && m.defaultValue)
          )
        );
      default:
        return true;
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) return;
    try {
      const templateJson = JSON.stringify(reportState);
      if (templateId) {
        const result = await updateTemplate.mutateAsync({
          id: templateId,
          data: {
            name: templateName,
            description: templateDescription || undefined,
            templateJson,
          },
        });
        setTemplateId(result.id);
      } else {
        const result = await createTemplate.mutateAsync({
          name: templateName,
          description: templateDescription || undefined,
          templateJson,
        });
        setTemplateId(result.id);
      }
      setSaveDialogOpen(false);
    } catch {
      // Error handled by mutation state
    }
  };

  if (!organizationId) {
    return (
      <Container maxWidth="lg">
        <Paper sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="h6" color="error">
            Por favor selecciona una organización para continuar.
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Paper elevation={3} sx={{ my: 4, p: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mb: 1 }}>
          <Typography variant="h4" component="h1">
            {isTemplateMode
              ? "Constructor de Plantillas de Reportes"
              : "Constructor de Reportes de Retos"}
          </Typography>
          <TemplateManager
            reportState={reportState}
            onLoadTemplate={handleLoadTemplate}
            currentTemplateId={templateId}
            onTemplateSaved={handleTemplateSaved}
          />
        </Box>

        <Stepper activeStep={activeStep} sx={{ mb: 4, mt: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box
          sx={{
            mt: 2,
            mb: 2,
            p: 2,
            border: "1px dashed grey",
            minHeight: "400px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "start",
          }}
        >
          {getStepContent()}
        </Box>

        <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
          <Button color="inherit" disabled={activeStep === 0} onClick={handleBack} sx={{ mr: 1 }}>
            Atrás
          </Button>
          <Box sx={{ flex: "1 1 auto" }} />
          {activeStep < steps.length - 1 && (
            <Button variant="contained" onClick={handleNext} disabled={!canProceed()}>
              Siguiente
            </Button>
          )}
          {isTemplateMode && activeStep === steps.length - 1 && (
            <Button
              variant="contained"
              onClick={() => setSaveDialogOpen(true)}
              disabled={!canProceed()}
            >
              Guardar Plantilla
            </Button>
          )}
        </Box>
      </Paper>

      <Dialog
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Guardar plantilla</DialogTitle>
        <DialogContent>
          {(createTemplate.isError || updateTemplate.isError) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Error al guardar la plantilla.
            </Alert>
          )}
          <TextField
            fullWidth
            label="Nombre"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            sx={{ mt: 1 }}
            required
          />
          <TextField
            fullWidth
            label="Descripción"
            value={templateDescription}
            onChange={(e) => setTemplateDescription(e.target.value)}
            sx={{ mt: 2 }}
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancelar</Button>
          <Button
            onClick={handleSaveTemplate}
            variant="contained"
            disabled={createTemplate.isPending || updateTemplate.isPending || !templateName.trim()}
          >
            {createTemplate.isPending || updateTemplate.isPending ? (
              <CircularProgress size={20} />
            ) : (
              "Guardar"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ReportBuilderPage;
